/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import type AnalyticsClient from '@atlassiansox/analytics-web-client/client';
import type {
	OperationalEventPayload,
	ScreenEventPayload,
	TrackEventPayload,
	UIEventPayload,
} from '@atlassiansox/analytics-web-client/types';
import type { Solutions } from '@jira/platform__common-constants/src/solutions-types.tsx';
import type { Theme } from '@jira/platform__common-constants/src/theme-preference.tsx';

export type Attributes = {
	[key: string]: boolean | string | number | undefined | null | Attributes;
};
export type UIViewedAttributes = {
	solutions?: Solutions[];
	theme?: Theme;
	OStheme?: Theme;
};
export type OriginTracingHandlerResponse = {
	originTracingAttributes: Attributes;
	taskSessionId?: string;
};
export type OriginTracingHandler = (encodedOrigin: string) => OriginTracingHandlerResponse;
export type OriginTracingHandlerMapping = {
	[param: string]: OriginTracingHandler;
};
export type InitAttrs = {
	orgId?: string;
	workspaceId?: string;
	product: string;
	environment: string;
	locale: string;
	tenantId?: string;
	userId?: string;
	subProduct?: string | (() => string);
	embeddedProduct?: () => string | false;
	callback?: AnalyticsClient['startUIViewedEvent'];
	shouldTriggerUIViewed?: boolean;
} & UIViewedAttributes;
export const ANALYTICS_EVENT_TYPES = ['SCREEN', 'UI', 'TRACK', 'OPERATIONAL'] as const;
export type AnalyticsEventType = (typeof ANALYTICS_EVENT_TYPES)[number];
/**
 * Identifies if the provided value meets the criteria to be considered an analytics event.
 * This is determined by evaluating certain properties of the value.
 */
export const isAnalyticsEvent = (value?: unknown): value is AnalyticsEvent =>
	typeof value === 'object' &&
	value !== null &&
	'type' in value &&
	typeof value.type === 'string' &&
	// oxlint-disable-next-line typescript/consistent-type-assertions
	ANALYTICS_EVENT_TYPES.includes(value.type as AnalyticsEventType);
interface AnalyticsEventObject<Type, Payload> {
	type: Type;
	payload: Payload;
}
export type ScreenEvent = AnalyticsEventObject<'SCREEN', ScreenEventPayload>;
export type UIEvent = AnalyticsEventObject<'UI', UIEventPayload>;
export type TrackEvent = AnalyticsEventObject<'TRACK', TrackEventPayload>;
export type OperationalEvent = AnalyticsEventObject<'OPERATIONAL', OperationalEventPayload>;
export type UnknownEvent = AnalyticsEventObject<unknown, unknown>;
export type AnalyticsEvent = ScreenEvent | UIEvent | TrackEvent | OperationalEvent;
export interface AnalyticsWebClient {
	init(attrs: InitAttrs): boolean;
	sendEvent(event: AnalyticsEvent | UnknownEvent): void;
	getInstance(): AnalyticsClient;
	isInitialized(): boolean;
	setUpdateAmplitudeSRClient(callbackFn: Function): void;
	setAmplitudeSRAttributesInjector(callbackFn: Function): void;
	setSubProduct(subproduct: string): void;
	setEmbeddedProduct(embeddedProduct: string): void;
	clearEmbeddedProduct(): void;
	stopApdexEvent(payload?: unknown): void;
	setOriginTracingHandlers(originParamHandlerMapping: OriginTracingHandlerMapping): void;
	setUIViewedAttributes(attributes: UIViewedAttributes): void;
	removeUIViewedAttribute(key: keyof UIViewedAttributes): void;
	clearUIViewedAttributes(): void;
}
